/**
 * intakeLanding — PURE logic for the `/intake/:linkId` landing route
 * (M5 Flag B, AD-022/AD-023). SDK-free so it is directly unit-testable.
 *
 * The landing route is the RECEIVING half of the intake-link loop: staff send
 * `https://portal.aayatanveda.in/intake/<linkId>` (S2 generateIntakeLink); a
 * recipient clicks it, signs in with the SAME Google/Apple flow the rest of the
 * portal uses (no new auth), the canonical client claim is minted by the LIVE
 * `resolveClientClaimsOnSignIn({uid,email})` callable, and they land in their
 * StageEngine intake form (routes/Intake.tsx, query-param driven).
 *
 * R-AUTH (AD-023): option (a) — pass the SIGNED-IN email to the LIVE callable
 * as-is; no functions edit. The CF matches the link by `linkEmail==email AND
 * status=='active'` server-side (admin SDK), so a freshly-signed-in user never
 * needs to read the link doc before claims exist (the rules forbid that read
 * pre-claim). The email-mismatch case (the client used a Google account != the
 * email Aayatan Veda has on file) returns `no_active_link` → a friendly
 * soft-fail telling them which email to use, never a dead-end (owner's
 * soften-hard-fails rule). This matches the existing Login.tsx guidance:
 * "sign in with the same email address you gave Aayatan Veda".
 */

/**
 * The services an intake link can carry. Kept SELF-CONTAINED here (NOT imported
 * from lib/intakeLinkStore — that S2 module is on a sibling feature branch and
 * not on main; this landing route must compile + ship on main independently of
 * the S2 send-half merge). Identical list to the S2 store's INTAKE_SERVICES.
 */
export const INTAKE_SERVICES = ['L1A', 'L1B', 'L2B', 'L3A', 'L3B'] as const;
export type IntakeService = (typeof INTAKE_SERVICES)[number];

/**
 * M5-S5: `birth_only` is a TARGETED intake (not a report tier) that captures
 * ONLY one person's birth details. It is valid as a link `service` and routes
 * to the People/birth stage pre-targeted to the link's `personId`. Kept OUT of
 * `INTAKE_SERVICES` (the report tiers) so the tier/name resolution is unaffected.
 */
export const BIRTH_ONLY_SERVICE = 'birth_only' as const;

/** The intakeLinks/{linkId} doc shape this route reads (S2 / AD-022). */
export interface IntakeLinkDoc {
  contactId?: string | null;
  cid?: string | null;
  clientId?: string | null;
  pid?: string | null;
  service?: string | null;
  propertyId?: string | null;
  /** M5-S5: the target person for a `birth_only` link (else absent/null). */
  personId?: string | null;
  linkEmail?: string | null;
  status?: string | null;
  /** Firestore Timestamp | {seconds} | ms-number | ISO string | null. */
  expiresAt?: unknown;
}

/**
 * Why a link is unusable (drives the soft-fail copy). `ok` = usable.
 *   missing       — no such doc id
 *   inactive      — status != 'active' (revoked / completed / expired-flag)
 *   expired       — expiresAt is in the past
 *   malformed     — doc present but missing contactId (cannot route)
 */
export type LinkVerdict = 'ok' | 'missing' | 'inactive' | 'expired' | 'malformed';

/** Normalise a Firestore-ish timestamp to epoch ms, or null if unreadable. */
export function toMillis(v: unknown): number | null {
  if (v == null) return null;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const t = Date.parse(v);
    return Number.isFinite(t) ? t : null;
  }
  if (typeof v === 'object') {
    const o = v as { toMillis?: () => number; toDate?: () => Date; seconds?: number };
    if (typeof o.toMillis === 'function') {
      try {
        const ms = o.toMillis();
        return Number.isFinite(ms) ? ms : null;
      } catch {
        return null;
      }
    }
    if (typeof o.toDate === 'function') {
      try {
        const d = o.toDate();
        const ms = d?.getTime?.();
        return Number.isFinite(ms) ? ms : null;
      } catch {
        return null;
      }
    }
    if (typeof o.seconds === 'number' && Number.isFinite(o.seconds)) {
      return o.seconds * 1000;
    }
  }
  return null;
}

/**
 * Verdict for a link doc. `exists=false` → 'missing'. Otherwise: status must be
 * 'active', expiresAt (if present + readable) must be in the future, and a
 * routable contactId must be present. A missing/unreadable expiresAt is treated
 * as NON-expiring (do not soft-fail on a clock we cannot read) — the status
 * field is the primary validity gate.
 */
export function linkVerdict(
  doc: IntakeLinkDoc | null | undefined,
  exists: boolean,
  nowMs: number,
): LinkVerdict {
  if (!exists || !doc) return 'missing';
  if (doc.status !== 'active') {
    // An explicitly-expired status reads as 'expired' (better copy); any other
    // non-active value (revoked / completed / unknown) reads as 'inactive'.
    return doc.status === 'expired' ? 'expired' : 'inactive';
  }
  const exp = toMillis(doc.expiresAt);
  if (exp != null && exp <= nowMs) return 'expired';
  const contactId = routableContactId(doc);
  if (!contactId) return 'malformed';
  return 'ok';
}

/** The Property entity id off the link doc, or null when the link is unscoped. */
export function routablePropertyId(doc: IntakeLinkDoc | null | undefined): string | null {
  const p = doc?.propertyId;
  return typeof p === 'string' && p.trim() ? p.trim() : null;
}

/** M5-S5 — is this a `birth_only` targeted link? (case-insensitive). */
export function isBirthOnlyLink(doc: IntakeLinkDoc | null | undefined): boolean {
  return (doc?.service || '').trim().toLowerCase() === BIRTH_ONLY_SERVICE;
}

/** M5-S5 — the target personId off the link doc, or null when absent. */
export function routablePersonId(doc: IntakeLinkDoc | null | undefined): string | null {
  const p = doc?.personId;
  return typeof p === 'string' && p.trim() ? p.trim() : null;
}

/** The canonical Contact/Client id off the link doc (contactId==cid==clientId). */
export function routableContactId(doc: IntakeLinkDoc | null | undefined): string | null {
  if (!doc) return null;
  const c =
    (typeof doc.contactId === 'string' && doc.contactId) ||
    (typeof doc.cid === 'string' && doc.cid) ||
    (typeof doc.clientId === 'string' && doc.clientId) ||
    null;
  return c && c.trim() ? c.trim() : null;
}

/** The service/tier off the link doc, defaulting to L1A when absent/unknown. */
export function linkService(doc: IntakeLinkDoc | null | undefined): IntakeService {
  const s = (doc?.service || '').trim().toUpperCase();
  return (INTAKE_SERVICES as readonly string[]).includes(s)
    ? (s as IntakeService)
    : 'L1A';
}

/**
 * Build the query-param path into the existing StageEngine intake form
 * (routes/Intake.tsx). Intake.tsx reads `?tier=&client=&stage=`; a client
 * session derives `client` from its own claim, but we pass it explicitly so the
 * form is unambiguous and so a deep-link re-open works. `stage=consent` lands on
 * the always-on Stage 0 (Intake.tsx resumes to the first incomplete stage if
 * the requested stage is unreachable, so this is just a sensible start anchor).
 */
export function intakeTargetPath(doc: IntakeLinkDoc): string {
  const client = routableContactId(doc) || '';
  const qs = new URLSearchParams();

  // M5-S5: a birth_only link does NOT carry a report tier — it carries the
  // `birth_only` mode + the target personId. The Intake route renders ONLY the
  // People/birth stage pre-targeted to that person. Consent still applies, but
  // for a single-person birth top-up the client is sent straight to the People
  // stage (Intake.tsx resumes to consent first if it is not yet satisfied).
  if (isBirthOnlyLink(doc)) {
    qs.set('tier', BIRTH_ONLY_SERVICE);
    if (client) qs.set('client', client);
    const personId = routablePersonId(doc);
    if (personId) qs.set('person', personId);
    qs.set('stage', 'person');
    return `/intake?${qs.toString()}`;
  }

  const tier = linkService(doc);
  const property = routablePropertyId(doc);
  qs.set('tier', tier);
  if (client) qs.set('client', client);
  // M5-S4: carry the property scope so the form links the intake to its
  // Property entity (location/floors are then sourced from that record).
  if (property) qs.set('property', property);
  qs.set('stage', 'consent');
  return `/intake?${qs.toString()}`;
}

/** The landing route phase — what the component should render / do next. */
export type LandingPhase =
  | 'loading' // auth session still restoring
  | 'signin' // no user yet → offer the existing sign-in
  | 'resolving' // signed in; minting claims + reading the link
  | 'route' // claims minted + link valid → navigate to the intake form
  | 'softfail'; // link unusable OR email-mismatch → friendly dead-end-free notice

/**
 * Decide the phase from the auth + resolution flags. PURE so the .tsx wiring
 * stays a thin shell over a tested decision table.
 *   - still restoring the Firebase session → 'loading'
 *   - no user → 'signin'
 *   - user present, claims not yet resolved (or link not yet read) → 'resolving'
 *   - a soft-fail reason was set → 'softfail'
 *   - claims minted AND link verdict 'ok' → 'route'
 */
export function landingPhase(input: {
  authLoading: boolean;
  hasUser: boolean;
  resolving: boolean;
  softFail: boolean;
  claimsMinted: boolean;
  verdict: LinkVerdict | null;
}): LandingPhase {
  if (input.authLoading) return 'loading';
  if (!input.hasUser) return 'signin';
  if (input.softFail) return 'softfail';
  if (input.resolving || input.verdict == null) return 'resolving';
  if (input.claimsMinted && input.verdict === 'ok') return 'route';
  return 'softfail';
}

/** Soft-fail headline + body keyed by the failure reason (no dead-ends). */
export interface SoftFailCopy {
  title: string;
  body: string;
}
export function softFailCopy(
  reason: LinkVerdict | 'email_mismatch' | 'error',
): SoftFailCopy {
  switch (reason) {
    case 'expired':
      return {
        title: 'This link has expired',
        body: 'Intake links are valid for a limited time. No problem — contact us and we’ll send you a fresh one.',
      };
    case 'inactive':
      return {
        title: 'This link is no longer active',
        body: 'It may have already been used or replaced. Contact us and we’ll get you a new link to your intake form.',
      };
    case 'missing':
      return {
        title: 'We couldn’t find this link',
        body: 'The link may be incomplete or mistyped. Contact us and we’ll send your intake form again.',
      };
    case 'email_mismatch':
      return {
        title: 'Almost there — sign in with the email we have on file',
        body: 'This intake link is tied to a specific email address. Please sign in with the same email address you gave Aayatan Veda, or contact us so we can update it.',
      };
    case 'malformed':
    case 'error':
    default:
      return {
        title: 'This link isn’t available right now',
        body: 'Something went wrong opening your intake form. Please try again in a moment, or contact us and we’ll help.',
      };
  }
}
